/// <reference types="vite/client" />
import React, { Suspense } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import CachePage from './pages/Cache';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';

// Agent Builder pages — lazy so the xyflow bundle ships separately.
const AgentBuilderLayout = React.lazy(() => import('./pages/agent-builder/AgentBuilderLayout'));
const AgentBuilderDashboard = React.lazy(() => import('./pages/agent-builder/Dashboard'));
const AgentBuilderTemplates = React.lazy(() => import('./pages/agent-builder/Templates'));
const AgentBuilderTextToAgent = React.lazy(() => import('./pages/agent-builder/TextToAgent'));
const AgentBuilderKnowledgeBase = React.lazy(() => import('./pages/agent-builder/KnowledgeBase'));
const AgentBuilderBuilder = React.lazy(() => import('./pages/agent-builder/Builder'));
const AgentBuilderTrace = React.lazy(() => import('./pages/agent-builder/Trace'));
const AgentBuilderCheckpoint = React.lazy(() => import('./pages/agent-builder/Checkpoint'));
const AgentBuilderDeploy = React.lazy(() => import('./pages/agent-builder/Deploy'));

const AgentBuilderFallback: React.FC = () => (
  <div className="min-h-[40vh] flex items-center justify-center">
    <div className="w-6 h-6 rounded-full border-2 border-primary-500 border-t-transparent animate-spin" />
  </div>
);

// Contexts
import { AuthProvider } from './contexts/AuthContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { NotificationProvider } from './contexts/NotificationContext';
import { ProjectProvider } from './contexts/ProjectContext';
import { MemoryProvider } from './components/memory';

// Hooks
import { useGlobalTracking } from './hooks/useGlobalTracking';
import { useEffect } from 'react';
import { setupCopyCodeFunction } from './utils/copyToClipboard';
// import { useLaunchDate } from './hooks/useLaunchDate';

// Components
import { Layout } from './components/common/Layout';
import { ProtectedRoute } from './components/auth/ProtectedRoute';
import { ErrorBoundary } from './components/common/ErrorBoundary';
import { TrackingConfiguration } from './components/analytics/TrackingConfiguration';
import { OnboardingCheck } from './components/common/OnboardingCheck';
// import { LaunchScreen } from './components/common/LaunchScreen';

// Pages
import Login from './pages/Login';
import Register from './pages/Register';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import OAuthCallback from './pages/OAuthCallback';
import PrivacyPolicy from './pages/PrivacyPolicy';
import TermsAndConditions from './pages/TermsAndConditions';
import ContactUs from './pages/ContactUs';
import ShippingPolicy from './pages/ShippingPolicy';
import CancellationsAndRefunds from './pages/CancellationsAndRefunds';
import MagicLinkConnect from './pages/MagicLinkConnect';
import VerifyEmail from './pages/VerifyEmail';
import ConfirmAccountClosure from './pages/ConfirmAccountClosure';
import AcceptInvite from './pages/AcceptInvite';
import { Dashboard } from './pages/Dashboard';
import Usage from './pages/Usage';
import { Analytics } from './pages/Analytics';
import { Optimization } from './pages/Optimization';
import { Settings } from './pages/Settings';
import { NotFound } from './pages/NotFound';
import { Profile } from './pages/Profile';
import { Alerts } from './pages/Alerts';
import CostAuditWizard from './pages/CostAuditWizard';
import Projects from './pages/Projects';
import JiraCallback from './pages/JiraCallback';
import DiscordCallback from './pages/DiscordCallback';
import SlackCallback from './pages/SlackCallback';
import PromptTemplates from './pages/PromptTemplates';
import TemplateUsage from './pages/TemplateUsage';
import TemplateAnalytics from './pages/TemplateAnalytics';
import { Integration } from './pages/Integration';
import { IntegrationsPage } from './pages/IntegrationsPage';
import Pricing from './pages/Pricing';
import { Subscription } from './pages/Subscription';
import SubscriptionSuccess from './pages/SubscriptionSuccess';
import SubscriptionCancel from './pages/SubscriptionCancel';
import ModelComparison from './pages/ModelComparison';
import AdvancedMonitoring from './pages/AdvancedMonitoring';
import Experimentation from './pages/Experimentation';
import Gateway from './pages/Gateway';
import AgentTrace from './pages/AgentTrace';
import KeyVault from './pages/KeyVault';
import PredictiveIntelligence from './pages/PredictiveIntelligence';
import Memory from './pages/Memory';
import Cache from './pages/Cache';
import { SessionsUnified } from './pages/SessionsUnified';
import { SessionDetail } from './pages/SessionDetail';
import { TelemetryDashboard } from './pages/telemetry/TelemetryDashboard';
import CostLake from './pages/CostLake';
import Webhooks from './pages/Webhooks';
import { Moderation } from './pages/Moderation';
import GitHubCallback from './pages/GitHubCallback';
import GitHubIntegrations from './pages/GitHubIntegrations';
import { GoogleIntegrations } from './pages/GoogleIntegrations';
import { AdminUserSpending } from './pages/AdminUserSpending';
import { AdminDashboard } from './pages/AdminDashboard';
import { AdminDiscountManagement } from './pages/AdminDiscountManagement';
import {
  Dashboard as DataNetworkEffectsDashboard,
  ModelPerformance,
  LearningLoop,
  AgentAnalytics,
  SemanticPatterns,
  GlobalBenchmarks
} from './pages/DataNetworkEffects';

// Component to handle global tracking inside the context providers
function AppContent() {
  // const { isLaunched } = useLaunchDate();

  // Initialize copy code functionality
  useEffect(() => {
    setupCopyCodeFunction();
  }, []);

  // Initialize global tracking for all interactions - temporarily disabled to debug login refresh
  useGlobalTracking();

  // Don't render app content until launched
  // if (!isLaunched) {
  //   return null;
  // }

  return (
    <ErrorBoundary>
      {/* Global tracking configuration component */}
      <TrackingConfiguration />
      <Routes>
        {/* Public routes */}
        <Route path="/login" element={<Login />} />
        <Route path="/register" element={<Register />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password/:token" element={<ResetPassword />} />
        <Route path="/oauth/callback" element={<OAuthCallback />} />
        <Route path="/privacy-policy" element={<PrivacyPolicy />} />
        <Route path="/terms" element={<TermsAndConditions />} />
        <Route path="/contact" element={<ContactUs />} />
        <Route path="/shipping-policy" element={<ShippingPolicy />} />
        <Route path="/cancellations-refunds" element={<CancellationsAndRefunds />} />
        <Route path="/connect/chatgpt" element={<MagicLinkConnect />} />
        <Route path="/verify-email/:token" element={<VerifyEmail />} />
        <Route path="/confirm-account-closure/:token" element={<ConfirmAccountClosure />} />
        <Route path="/accept-invite/:token" element={<AcceptInvite />} />
        <Route path="/github/success" element={<GitHubCallback />} />
        <Route path="/github/error" element={<GitHubCallback />} />
        <Route path="/integrations/jira/success" element={<JiraCallback />} />
        <Route path="/integrations/jira/error" element={<JiraCallback />} />
        <Route path="/integrations/discord/success" element={<DiscordCallback />} />
        <Route path="/integrations/discord/error" element={<DiscordCallback />} />
        <Route path="/integrations/slack/success" element={<SlackCallback />} />
        <Route path="/integrations/slack/error" element={<SlackCallback />} />
        <Route path="/subscription/success" element={<SubscriptionSuccess />} />
        <Route path="/subscription/cancel" element={<SubscriptionCancel />} />

        {/* Protected routes */}
        <Route
          path="/"
          element={
            <ProtectedRoute>
              <OnboardingCheck>
                <Layout />
              </OnboardingCheck>
            </ProtectedRoute>
          }
        >
          <Route
            index
            element={
              <Navigate
                to="/dashboard"
                replace
              />
            }
          />
          <Route path="/dashboard" element={<Dashboard />} />
          <Route path="/dashboard/:conversationId" element={<Dashboard />} />
          <Route path="/chat/:conversationId" element={<Dashboard />} />
          <Route path="/cache" element={<CachePage />} />
          <Route path="usage" element={<Usage />} />
          <Route
            path="analytics"
            element={<Analytics />}
          />
          <Route
            path="pricing"
            element={<Pricing />}
          />
          <Route
            path="subscription"
            element={<Subscription />}
          />
          <Route
            path="model-comparison"
            element={<ModelComparison />}
          />
          <Route
            path="optimizations"
            element={<Optimization />}
          />
          <Route
            path="cache"
            element={<Cache />}
          />
          <Route
            path="optimizations/wizard"
            element={<CostAuditWizard />}
          />
          <Route
            path="projects"
            element={<Projects />}
          />
          <Route
            path="templates"
            element={<PromptTemplates />}
          />
          <Route
            path="templates/use"
            element={<TemplateUsage />}
          />
          <Route
            path="templates/analytics"
            element={<TemplateAnalytics />}
          />
          <Route
            path="templates/:templateId"
            element={<TemplateUsage />}
          />
          <Route
            path="advanced-monitoring"
            element={<AdvancedMonitoring />}
          />
          <Route
            path="experimentation"
            element={<Experimentation />}
          />
          <Route
            path="gateway"
            element={<Gateway />}
          />
          <Route
            path="agent-trace"
            element={<AgentTrace />}
          />
          <Route
            path="workflows"
            element={<Navigate to="/agent-trace" replace />}
          />

          {/* Agent Builder — nested inside the main CostKatana Layout */}
          <Route
            path="agent-builder"
            element={
              <Suspense fallback={<AgentBuilderFallback />}>
                <AgentBuilderLayout />
              </Suspense>
            }
          >
            <Route index element={<Suspense fallback={<AgentBuilderFallback />}><AgentBuilderDashboard /></Suspense>} />
            <Route path="templates" element={<Suspense fallback={<AgentBuilderFallback />}><AgentBuilderTemplates /></Suspense>} />
            <Route path="text-to-agent" element={<Suspense fallback={<AgentBuilderFallback />}><AgentBuilderTextToAgent /></Suspense>} />
            <Route path="knowledge-base" element={<Suspense fallback={<AgentBuilderFallback />}><AgentBuilderKnowledgeBase /></Suspense>} />
            <Route path=":agentId" element={<Suspense fallback={<AgentBuilderFallback />}><AgentBuilderBuilder /></Suspense>} />
            <Route path=":agentId/runs/:runId" element={<Suspense fallback={<AgentBuilderFallback />}><AgentBuilderTrace /></Suspense>} />
            <Route path=":agentId/runs/:runId/checkpoint" element={<Suspense fallback={<AgentBuilderFallback />}><AgentBuilderCheckpoint /></Suspense>} />
            <Route path=":agentId/deploy" element={<Suspense fallback={<AgentBuilderFallback />}><AgentBuilderDeploy /></Suspense>} />
          </Route>
          <Route
            path="key-vault"
            element={<KeyVault />}
          />
          <Route
            path="integrations"
            element={<IntegrationsPage />}
          />
          <Route
            path="moderation"
            element={<Moderation />}
          />
          <Route
            path="security"
            element={<Navigate to="/moderation" replace />}
          />
          <Route
            path="predictive-intelligence"
            element={<PredictiveIntelligence />}
          />
          <Route
            path="memory"
            element={<Memory />}
          />
          <Route
            path="webhooks"
            element={<Webhooks />}
          />
          <Route
            path="sessions"
            element={<SessionsUnified />}
          />
          <Route
            path="sessions/replay/:sessionId"
            element={<SessionsUnified />}
          />
          <Route
            path="sessions/:id"
            element={<SessionDetail />}
          />
          <Route
            path="telemetry"
            element={<TelemetryDashboard />}
          />
          <Route
            path="cost-lake"
            element={<CostLake />}
          />
          <Route
            path="settings"
            element={<Navigate to="/settings/profile" replace />}
          />
          <Route
            path="settings/:tab"
            element={<Settings />}
          />
          <Route path="profile" element={<Profile />} />
          <Route path="alerts" element={<Alerts />} />
          <Route
            path="integration"
            element={<Integration />}
          />
          <Route
            path="github"
            element={<GitHubIntegrations />}
          />
          <Route
            path="google"
            element={<GoogleIntegrations />}
          />
          <Route
            path="admin/user-spending"
            element={
              <ProtectedRoute requireAdmin={true}>
                <AdminUserSpending />
              </ProtectedRoute>
            }
          />
          <Route
            path="admin/dashboard"
            element={
              <ProtectedRoute requireAdmin={true}>
                <AdminDashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="admin/discounts"
            element={
              <ProtectedRoute requireAdmin={true}>
                <AdminDiscountManagement />
              </ProtectedRoute>
            }
          />
          {/* Data Network Effects Routes (admin only) */}
          <Route
            path="admin/data-network-effects"
            element={
              <ProtectedRoute requireAdmin={true}>
                <DataNetworkEffectsDashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="admin/data-network-effects/models"
            element={
              <ProtectedRoute requireAdmin={true}>
                <ModelPerformance />
              </ProtectedRoute>
            }
          />
          <Route
            path="admin/data-network-effects/learning-loop"
            element={
              <ProtectedRoute requireAdmin={true}>
                <LearningLoop />
              </ProtectedRoute>
            }
          />
          <Route
            path="admin/data-network-effects/agents"
            element={
              <ProtectedRoute requireAdmin={true}>
                <AgentAnalytics />
              </ProtectedRoute>
            }
          />
          <Route
            path="admin/data-network-effects/patterns"
            element={
              <ProtectedRoute requireAdmin={true}>
                <SemanticPatterns />
              </ProtectedRoute>
            }
          />
          <Route
            path="admin/data-network-effects/benchmarks"
            element={
              <ProtectedRoute requireAdmin={true}>
                <GlobalBenchmarks />
              </ProtectedRoute>
            }
          />
        </Route>

        {/* 404 page */}
        <Route path="*" element={<NotFound />} />
      </Routes>
    </ErrorBoundary>
  );
}

function App() {
  return (
    <NotificationProvider>
      <AuthProvider>
        <ThemeProvider>
          <ProjectProvider>
            <MemoryProvider>
              {/* <LaunchScreen /> */}
              <AppContent />
            </MemoryProvider>
          </ProjectProvider>
        </ThemeProvider>
        <ReactQueryDevtools initialIsOpen={false} />
      </AuthProvider>
    </NotificationProvider>
  );
}

export default App;